import Box from '@mui/material/Box';
import { Group } from '@visx/group';
import { scaleTime } from '@visx/scale';
import { useMemo } from 'react';
import { QUALITY_TIMELINE } from '../../consts';
import { VisibleTimeRange } from '../../lib/visible-time-range';
import { TimelineAxisTop } from './TimelineAxisTop';

export type TimelineAxisStripProps = {
  width: number;
  /** Interval mapped to the chart inner width (full report range or zoomed window). */
  timeRange: VisibleTimeRange;
  /** IANA zone for tick labels; omit for browser-local wall time. */
  timeZone?: string;
  /**
   * When set, the strip is `position: sticky` with this `top` offset (e.g. below the global report header).
   */
  stickyTop?: number | string;
  /**
   * When true, the SVG omits the left station-label gutter (`QUALITY_TIMELINE.margin.left`) so the strip
   * fits beside a sibling that already reserves that width (e.g. timezone group header). Time scale matches
   * `TimelineChart` because `innerWidth` is still derived from the full chart `width`.
   */
  trimLeftGutter?: boolean;

  height?: number;
};

/**
 * Top time axis strip: same margins and inner width as `TimelineChart` and the range brush
 * minimap so ticks line up with the chart body, brush overview, or both.
 */
export function TimelineAxisStrip({
  width,
  timeRange,
  timeZone,
  stickyTop,
  trimLeftGutter = false,
  height,
}: TimelineAxisStripProps) {
  const margin = QUALITY_TIMELINE.margin;
  const visibleStart = timeRange.from;
  const visibleEnd = timeRange.to;
  const innerWidth = width - margin.left - margin.right;
  const groupLeft = trimLeftGutter ? 0 : margin.left;
  const svgWidth = trimLeftGutter ? width - margin.left : width;

  const xScale = useMemo(
    () =>
      scaleTime<number>({
        domain: [visibleStart, visibleEnd],
        range: [0, innerWidth],
      }),
    [visibleStart, visibleEnd, innerWidth],
  );

  if (
    width < 10 ||
    innerWidth < 1 ||
    Number.isNaN(visibleStart.getTime()) ||
    Number.isNaN(visibleEnd.getTime()) ||
    !(visibleEnd.getTime() > visibleStart.getTime())
  ) {
    return null;
  }

  const inner = (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <svg width={svgWidth} height={height || margin.top}>
        <Group left={groupLeft} top={margin.top}>
          <TimelineAxisTop
            scale={xScale}
            stroke={QUALITY_TIMELINE.chartColors.grid}
            tickStroke={QUALITY_TIMELINE.chartColors.grid}
            tickLabelFill={QUALITY_TIMELINE.chartColors.axisTick}
            timeZone={timeZone}
          />
        </Group>
      </svg>
    </div>
  );

  if (stickyTop === undefined) {
    return inner;
  }

  return (
    <Box
      sx={(theme) => ({
        position: 'sticky',
        top: stickyTop,
        zIndex: theme.zIndex.appBar - 2,
        bgcolor: 'background.paper',
      })}
    >
      {inner}
    </Box>
  );
}
